/**
 * DualTimelinePanel Component
 *
 * Side-by-side CURRENT/REVISED timeline layout with:
 * - Synchronized zoom/pan via shared context
 * - Shared timeline axis
 * - Toggle mode for expand/collapse
 */

import type { ReactNode } from 'react'
import { useState, useCallback, useMemo } from 'react'
import { UpOutlined, DownOutlined } from '@ant-design/icons'
import { TimelineViewport } from './TimelineViewport'
import { TimelineAxis } from './TimelineAxis'
import { useTimelineContext } from '../context/TimelineContext'
import type { DualTimelinePanelProps } from '../types'

interface ExtendedDualTimelinePanelProps extends DualTimelinePanelProps {
  /** Display mode for the panel */
  displayMode: 'dual' | 'single' | 'toggle'
  /** Controlled expanded state */
  expanded?: boolean
  /** Initial expanded state (for uncontrolled mode) */
  defaultExpanded?: boolean
  /** Callback when expanded state changes */
  onExpandedChange?: (expanded: boolean) => void
  /** Animation duration in ms */
  animationDuration?: number
  /** Domain for positioning (optional - uses context if not provided) */
  domain?: {
    minDate: number
    maxDate: number
  }
  /** Currently hovered segment UID (for bidirectional highlighting) */
  hoveredSegmentUid?: string | null
  /** Callback when segment hover state changes */
  onSegmentHover?: (uid: string | null) => void
  /** Optional status message to show next to the toggle when collapsed */
  collapsedStatusMessage?: ReactNode
}

/**
 * DualTimelinePanel Component
 */
export function DualTimelinePanel({
  beforeData,
  afterData,
  newRecord: _newRecord,
  width,
  timelineHeight = 48,
  gap = 8,
  onSegmentClick,
  selectedSegmentUid,
  hoveredSegmentUid,
  onSegmentHover,
  showBackgroundSegments = false,
  displayMode,
  expanded: controlledExpanded,
  defaultExpanded = true,
  onExpandedChange,
  animationDuration = 250,
  domain: propDomain,
  collapsedStatusMessage,
}: ExtendedDualTimelinePanelProps) {
  // Get zoom/pan state from context
  const { state } = useTimelineContext()
  const domain = propDomain || state.domain
  // Internal expanded state (for uncontrolled mode)
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)

  // Determine if we're in controlled mode
  const isControlled = controlledExpanded !== undefined
  const isExpanded = isControlled ? controlledExpanded : internalExpanded

  // Toggle handler
  const handleToggle = useCallback(() => {
    const newExpanded = !isExpanded
    if (!isControlled) {
      setInternalExpanded(newExpanded)
    }
    onExpandedChange?.(newExpanded)
  }, [isExpanded, isControlled, onExpandedChange])

  // Determine what to show based on display mode
  const showBefore = displayMode === 'dual' || (displayMode === 'toggle' && isExpanded)
  const showToggle = displayMode === 'toggle'

  // Animation styles
  const beforeSectionStyle = useMemo(() => ({
    maxHeight: showBefore ? `${timelineHeight + gap + 24}px` : '0',
    opacity: showBefore ? 1 : 0,
    transition: animationDuration > 0
      ? `max-height ${animationDuration}ms ease-out, opacity ${animationDuration}ms ease-out`
      : 'none',
    overflow: 'hidden',
  }), [showBefore, timelineHeight, gap, animationDuration])

  return (
    <div className="dual-timeline-panel" style={{ width }}>
      {/* CURRENT timeline (collapsible in toggle mode) */}
      {(displayMode !== 'single') && (
        <div
          className={`dual-timeline-panel__before ${showBefore ? 'expanded' : 'collapsed'}`}
          style={beforeSectionStyle}
        >
          <TimelineViewport
            segments={beforeData.segments}
            backgroundSegments={beforeData.backgroundSegments}
            showBackgroundSegments={showBackgroundSegments}
            label={
              <div className="dual-timeline-panel__label-row">
                <span>CURRENT</span>
                {showToggle && (
                  <button
                    className="dual-timeline-panel__toggle"
                    onClick={handleToggle}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? 'Collapse timeline comparison' : 'Expand timeline comparison'}
                  >
                    <UpOutlined />
                  </button>
                )}
              </div>
            }
            height={timelineHeight}
            onSegmentClick={onSegmentClick}
            selectedSegmentUid={selectedSegmentUid}
            hoveredSegmentUid={hoveredSegmentUid}
            onSegmentHover={onSegmentHover}
            domain={domain}
            containerWidth={width}
          />
        </div>
      )}

      {/* REVISED timeline (always visible) */}
      <div className="dual-timeline-panel__after">
        <TimelineViewport
          segments={afterData.segments}
          backgroundSegments={afterData.backgroundSegments}
          showBackgroundSegments={showBackgroundSegments}
          label={
            <div className="dual-timeline-panel__label-row">
              <span>REVISED</span>
              {!showBefore && collapsedStatusMessage && (
                <span className="dual-timeline-panel__status-message">
                  {collapsedStatusMessage}
                </span>
              )}
              {showToggle && !showBefore && (
                <button
                  className="dual-timeline-panel__toggle"
                  onClick={handleToggle}
                  aria-expanded={isExpanded}
                  aria-label="Expand timeline comparison"
                >
                  <DownOutlined />
                </button>
              )}
            </div>
          }
          height={timelineHeight}
          onSegmentClick={onSegmentClick}
          selectedSegmentUid={selectedSegmentUid}
          hoveredSegmentUid={hoveredSegmentUid}
          onSegmentHover={onSegmentHover}
          domain={domain}
          containerWidth={width}
        />
      </div>

      {/* Shared timeline axis */}
      <TimelineAxis
        minDate={domain.minDate}
        maxDate={domain.maxDate}
        containerWidth={width}
        transform={state.transform}
      />
    </div>
  )
}

export default DualTimelinePanel
